import { z } from 'zod';
const timestamp = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const id = z.number().int().positive().max(4_294_967_295);
const price = z
    .string()
    .regex(/^-?(0|[1-9]\d{0,18})$/)
    .refine((v) => BigInt(v) >= -9223372036854775808n && BigInt(v) < 9223372036854775807n);
const positiveRaw = price.refine((v) => BigInt(v) > 0);
const source = {
    sourceUrl: z
        .url()
        .max(1024)
        .refine((v) => new URL(v).protocol === 'https:'),
    sourceReference: z.string().trim().min(1).max(120)
};
export const futuresContractLastTradeInputSchema = z
    .object({
    contractId: id,
    lastTradingTimestamp: timestamp.refine((v) => v > 0),
    availableTimestamp: timestamp,
    ...source
})
    .strict();
export const futuresContractLastTradeSchema = futuresContractLastTradeInputSchema.extend({ id, productId: id });
/** An exclusive exchange deadline, resolved only from evidence public at the decision time. */
export function resolveFuturesLastTrade(rows, productId, contractId, atTimestamp) {
    id.parse(productId);
    id.parse(contractId);
    timestamp.parse(atTimestamp);
    if (rows.length > 32)
        throw new Error('Futures last-trade evidence exceeds the bounded revision count');
    const candidates = rows
        .map((v) => futuresContractLastTradeSchema.parse(v))
        .filter((v) => v.productId === productId && v.contractId === contractId && v.availableTimestamp <= atTimestamp)
        .sort((a, b) => b.availableTimestamp - a.availableTimestamp);
    const selected = candidates[0];
    if (!selected)
        return { state: 'unknown' };
    if (candidates.some((v, i) => i > 0 && v.availableTimestamp === candidates[i - 1]?.availableTimestamp))
        throw new Error('Ambiguous futures last-trade revisions');
    return {
        state: atTimestamp < selected.lastTradingTimestamp ? 'beforeLastTrade' : 'tradingEnded',
        lastTradeId: selected.id,
        lastTradingTimestamp: selected.lastTradingTimestamp
    };
}
export const futuresProductTermsSchema = z
    .object({
    id,
    productId: id,
    effectiveFromTimestamp: timestamp,
    effectiveUntilTimestamp: timestamp.nullable(),
    availableTimestamp: timestamp,
    tickSizeRaw: positiveRaw,
    pointValueRaw: positiveRaw,
    settlementCurrency: z.string().regex(/^[A-Z]{3}$/),
    settlementType: z.enum(['cash', 'physical']),
    ...source
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.effectiveUntilTimestamp !== null && v.effectiveUntilTimestamp <= v.effectiveFromTimestamp)
        ctx.addIssue({ code: 'custom', message: 'Invalid effective futures terms clocks' });
});
export const futuresSessionDaySchema = z
    .object({
    id,
    productId: id,
    sessionLabel: z.iso.date(),
    tradeDate: z.iso.date().nullable(),
    startTimestamp: timestamp,
    endTimestamp: timestamp,
    availableTimestamp: timestamp,
    ...source,
    intervals: z.array(z.object({ startTimestamp: timestamp, endTimestamp: timestamp }).strict()).max(16)
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.endTimestamp <= v.startTimestamp ||
        // CME's July 2020 schedule assigns Thursday evening through Monday
        // to one trade date, including the closed holiday weekend.
        v.endTimestamp - v.startTimestamp > 4 * 86400 ||
        v.startTimestamp % 60 ||
        v.endTimestamp % 60)
        ctx.addIssue({ code: 'custom', message: 'Invalid covered futures session interval' });
    if ((v.tradeDate === null) !== (v.intervals.length === 0))
        ctx.addIssue({
            code: 'custom',
            message: 'Open sessions require an official trade date; closed days have no matching intervals'
        });
    let previous = v.startTimestamp;
    for (const interval of v.intervals) {
        if (interval.startTimestamp < previous ||
            interval.endTimestamp <= interval.startTimestamp ||
            interval.endTimestamp > v.endTimestamp ||
            interval.startTimestamp % 60 ||
            interval.endTimestamp % 60)
            ctx.addIssue({ code: 'custom', message: 'Unordered, overlapping or out-of-owner futures matching interval' });
        previous = interval.endTimestamp;
    }
});
export function resolveFuturesProductTerms(rows, productId, atTimestamp) {
    id.parse(productId);
    timestamp.parse(atTimestamp);
    if (rows.length > 32)
        throw new Error('Futures terms exceed the bounded revision count');
    const candidates = rows
        .map((v) => futuresProductTermsSchema.parse(v))
        .filter((v) => v.productId === productId &&
        v.availableTimestamp <= atTimestamp &&
        v.effectiveFromTimestamp <= atTimestamp &&
        (v.effectiveUntilTimestamp === null || atTimestamp < v.effectiveUntilTimestamp));
    // Revisions may amend the same effective owner; different overlapping owners are ambiguous.
    candidates.sort((a, b) => b.availableTimestamp - a.availableTimestamp);
    const selected = candidates[0];
    if (!selected)
        throw new Error('Verified futures pricing terms are unavailable');
    if (candidates.some((v) => v.effectiveFromTimestamp !== selected.effectiveFromTimestamp ||
        (v.id !== selected.id && v.availableTimestamp === selected.availableTimestamp)))
        throw new Error('Ambiguous overlapping futures pricing terms');
    return selected;
}
export function resolveFuturesScheduledMinute(rows, productId, atTimestamp) {
    id.parse(productId);
    timestamp.parse(atTimestamp);
    if (atTimestamp % 60 || rows.length > 32)
        throw new Error('Futures calendar lookup requires a bounded exact minute');
    const candidates = rows
        .map((v) => futuresSessionDaySchema.parse(v))
        .filter((v) => v.productId === productId &&
        v.availableTimestamp <= atTimestamp &&
        v.startTimestamp <= atTimestamp &&
        atTimestamp < v.endTimestamp);
    candidates.sort((a, b) => b.availableTimestamp - a.availableTimestamp);
    const selected = candidates[0];
    if (!selected || atTimestamp + 60 > selected.endTimestamp)
        return { state: 'unknown' };
    if (candidates.some((v) => v.sessionLabel !== selected.sessionLabel ||
        v.startTimestamp !== selected.startTimestamp ||
        v.endTimestamp !== selected.endTimestamp ||
        (v.id !== selected.id && v.availableTimestamp === selected.availableTimestamp)))
        throw new Error('Ambiguous overlapping futures calendar owners');
    const base = { sessionDayId: selected.id, sessionLabel: selected.sessionLabel, tradeDate: selected.tradeDate };
    if (selected.tradeDate !== null &&
        selected.intervals.some((v) => v.startTimestamp <= atTimestamp && atTimestamp + 60 <= v.endTimestamp))
        return { ...base, state: 'scheduledOpen', tradeDate: selected.tradeDate };
    return { ...base, state: 'scheduledClosed' };
}
/** Linear price-change money in settlement currency's 1e-9 units, before fees, margin or settlement cash. */
export function futuresPriceChangeMoneyRaw(input) {
    const terms = futuresProductTermsSchema.parse(input.terms);
    const from = BigInt(price.parse(input.fromPriceRaw)), to = BigInt(price.parse(input.toPriceRaw));
    const quantity = BigInt(id.parse(input.contracts));
    if (input.positionSide !== 'long' && input.positionSide !== 'short')
        throw new Error('Invalid futures position ownership');
    const tick = BigInt(terms.tickSizeRaw);
    if (from % tick || to % tick)
        throw new Error('Futures price is off the verified outright tick');
    const raw = (to - from) * BigInt(terms.pointValueRaw) * quantity * (input.positionSide === 'long' ? 1n : -1n);
    if (raw % 1000000000n)
        throw new Error('Futures price-change money requires an explicit monetary rounding policy');
    // Keep the monetary result representable at the canonical signed BIGINT
    // boundary, just like raw prices. Never let a caller round an oversized
    // result through Number before persistence.
    return price.parse((raw / 1000000000n).toString());
}
//# sourceMappingURL=futuresReference.js.map